import { Mail, MapPin } from "lucide-react";
import { Map } from "@/components/Map";
import { usePageTracking } from "../hooks/usePageTracking";
import { useTranslation } from "react-i18next";
import { PageSeo } from "@/components/seo/PageSeo";
import { CAIRE_POSTAL_ADDRESS } from "@/components/seo/organization-data";
import { BASE_URL } from "@/config/env";
import { useLanguage } from "@/hooks/useLanguage";
import { PageHero } from "@/components/marketing/PageHero";
import { ContactFounderVisual } from "@/components/marketing/heroVisuals/ContactFounderVisual";
import { EditorialCard } from "@/components/marketing/cards";
import { bookDemoCtaLabel, bookDemoHref } from "@/config/platformAccess";

const Contact = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(["common", "contact"], { lng: language });
  const isSv = language === "sv";
  const isEn = !isSv;
  const fallback = isSv
    ? {
        pageTitle: "Kontakta oss",
        metaDescription:
          "Kontakta Caire för frågor om vår AI-drivna schemaläggningslösning för hemtjänst. Boka demo, be om mer information eller kom i kontakt med vårt team.",
        infoTitle: "Kontaktinformation",
        email: "E-post",
        address: "Adress",
        streetAddress: CAIRE_POSTAL_ADDRESS.streetAddress,
        postalCode: `${CAIRE_POSTAL_ADDRESS.postalCode} ${CAIRE_POSTAL_ADDRESS.addressLocality}`,
        country: "Sverige",
        fullAddress: `${CAIRE_POSTAL_ADDRESS.streetAddress}, ${CAIRE_POSTAL_ADDRESS.postalCode} ${CAIRE_POSTAL_ADDRESS.addressLocality}, Sverige`,
        aboutTitle: "Om Caire",
        aboutDescription:
          "Vi erbjuder innovativa lösningar för hemtjänst med fokus på AI-driven schemaläggning och resursoptimering. Kontakta oss för att diskutera hur vi kan hjälpa er organisation.",
      }
    : {
        pageTitle: "Contact us",
        metaDescription:
          "Contact Caire about AI-driven scheduling for home care. Book a demo, request information, or get in touch with our team.",
        infoTitle: "Contact information",
        email: "Email",
        address: "Address",
        streetAddress: CAIRE_POSTAL_ADDRESS.streetAddress,
        postalCode: `${CAIRE_POSTAL_ADDRESS.postalCode} ${CAIRE_POSTAL_ADDRESS.addressLocality}`,
        country: "Sweden",
        fullAddress: `${CAIRE_POSTAL_ADDRESS.streetAddress}, ${CAIRE_POSTAL_ADDRESS.postalCode} ${CAIRE_POSTAL_ADDRESS.addressLocality}, Sweden`,
        aboutTitle: "About Caire",
        aboutDescription:
          "We build AI-driven scheduling and resource optimization for home care. Contact us to discuss how we can help your organization.",
      };
  usePageTracking(t("contact:contact.page.title", fallback.pageTitle));

  return (
    <div className="min-h-screen bg-caire-page">
      {/*
        No Organization node here. This was a second copy of the site-wide
        entity — and carried postcode "13151" where the canonical node says
        "131 58" — so the page shipped two conflicting Organizations. The
        visible copy is driven by the locale files, not by these fallbacks, so
        those were corrected too. The
        customer-support contactPoint now lives on the single Organization in
        organization-data.ts.
      */}
      <PageSeo
        title={t("contact:contact.meta.title", fallback.pageTitle)}
        description={t(
          "contact:contact.meta.description",
          fallback.metaDescription,
        )}
        ogImage={`${BASE_URL}/images/og/contact.jpg`}
        canonicalPath={isSv ? "/kontakt" : "/en/contact"}
        alternateLanguageUrl={isSv ? "/en/contact" : "/kontakt"}
        language={language}
        // PageSeo owns WebPage + BreadcrumbList; <PageSchema> emitted a
        // second pair into the body and is removed.
        breadcrumbs={[
          {
            name: isSv ? "Hem" : "Home",
            item: `${BASE_URL}${isSv ? "/" : "/en"}`,
            position: 1,
          },
          {
            name: t("contact:contact.meta.title", fallback.pageTitle),
            item: `${BASE_URL}${isSv ? "/kontakt" : "/en/contact"}`,
            position: 2,
          },
        ]}
      />

      <PageHero
        eyebrow={isEn ? "Contact" : "Kontakt"}
        title={
          isEn ? (
            <>
              Contact <span className="text-coral-text">Caire</span>
            </>
          ) : (
            <>
              Kontakta <span className="text-coral-text">Caire</span>
            </>
          )
        }
        description={
          isEn
            ? "Book a walkthrough, ask about pricing, or request security documentation for a procurement. You'll get answers from someone who knows home care planning."
            : "Boka en genomgång, fråga om pris eller be om säkerhetsunderlag inför en upphandling. Ni får svar av någon som kan hemtjänstplanering."
        }
        primary={{
          label: bookDemoCtaLabel(isEn ? "en" : "sv"),
          href: bookDemoHref(isEn ? "en" : "sv"),
          external: true,
        }}
        secondary={{
          label: isEn ? "FAQ" : "Vanliga frågor",
          href: isEn ? "/en/faq" : "/vanliga-fragor",
        }}
        visual={<ContactFounderVisual isEn={isEn} />}
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-16 pb-20">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold tracking-tight text-caire-ink">
            {t("contact:contact.info.title", fallback.infoTitle)}
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <EditorialCard
              domain="human"
              icon={<Mail className="h-5 w-5" />}
              title={t("contact:contact.info.email", fallback.email)}
              body="info@caire.se"
              footer={
                <a
                  href="mailto:info@caire.se"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-caire-action"
                >
                  info@caire.se
                </a>
              }
            />

            <EditorialCard
              domain="society"
              icon={<MapPin className="h-5 w-5" />}
              title={t("contact:contact.info.address", fallback.address)}
              body={[
                t("contact:contact.info.streetAddress", fallback.streetAddress),
                t("contact:contact.info.postalCode", fallback.postalCode),
                t("contact:contact.info.country", fallback.country),
              ].join(", ")}
            />
          </div>

          <div className="mt-6">
            <EditorialCard
              domain="continuity"
              title={t("contact:contact.about.title", fallback.aboutTitle)}
              body={t(
                "contact:contact.about.description",
                fallback.aboutDescription,
              )}
            />
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-caire-hairline">
            <Map
              address={t(
                "contact:contact.info.fullAddress",
                fallback.fullAddress,
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
